import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts"
import { useCurrency } from "@/context/currency/currency-context"
import { InfoTip } from "@/components/custom/fire/info-tip"
import type { FireIncomeMonth } from "@/types/fire"

interface FireIncomeHistoryProps {
	history: FireIncomeMonth[]
}

function monthLabel(year: number, month: number) {
	return new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" })
}

export function FireIncomeHistory({ history }: FireIncomeHistoryProps) {
	const { format, convert } = useCurrency()

	// Group by year+month, sum across currencies (convert each to display currency)
	const grouped = new Map<string, { label: string; amount: number }>()
	for (const entry of history) {
		const key = `${entry.year}-${String(entry.month).padStart(2, "0")}`
		const existing = grouped.get(key)
		const converted = convert(entry.amount, entry.currency)
		if (existing) {
			existing.amount += converted
		} else {
			grouped.set(key, { label: monthLabel(entry.year, entry.month), amount: converted })
		}
	}

	const chartData = Array.from(grouped.entries())
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([, v]) => v)

	if (chartData.length === 0) return null

	return (
		<div className="space-y-2">
			<p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
				Monthly Income History (last 24 months)
				<InfoTip content="Actual dividends received each month across all portfolios, converted to your display currency." />
			</p>
			<div className="h-40 [&_.recharts-wrapper]:outline-none [&_svg]:outline-none">
				<ResponsiveContainer width="100%" height="100%">
					<BarChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }} tabIndex={-1}>
						<XAxis
							dataKey="label"
							tick={{ fontSize: 10 }}
							interval="preserveStartEnd"
							axisLine={false}
							tickLine={false}
						/>
						<YAxis
							tickFormatter={(v) => format(v, { compact: true, skipConversion: true })}
							tick={{ fontSize: 10 }}
							width={55}
							axisLine={false}
							tickLine={false}
						/>
						<RechartsTooltip
							formatter={(v: number | undefined) => [
								v != null ? format(v, { skipConversion: true }) : "—",
								"Income",
							]}
							cursor={false}
						/>
						<Bar
							dataKey="amount"
							fill="#0ea5e9"
							radius={[2, 2, 0, 0]}
							activeBar={{ fill: "#0ea5e9", stroke: "none", strokeWidth: 0 }}
						/>
					</BarChart>
				</ResponsiveContainer>
			</div>
		</div>
	)
}
